import {
    createContext,
    useCallback,
    useContext,
    useRef,
    useState,
    ReactNode,
} from "react"

type ToastType = "success" | "error" | "info"

interface Toast {
    id: number
    type: ToastType
    message: string
}

interface ToastContextValue {
    notify: (message: string, type?: ToastType) => void
}

const ToastContext = createContext<ToastContextValue>({ notify: () => {} })

export const useToast = () => useContext(ToastContext)

const TONE = {
    success: { border: "#2e9e5b", ring: "#e6f5ec" },
    error: { border: "#FF3366", ring: "#fdeef0" },
    info: { border: "#486af6", ring: "#e8edff" },
}

const ToastIcon = ({ type }: { type: ToastType }) => {
    const color = TONE[type].border
    if (type === "success") {
        return (
            <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 shrink-0">
                <circle cx="12" cy="12" r="9" />
                <path d="M8.5 12.5l2.5 2.5 4.5-5" />
            </svg>
        )
    }
    if (type === "error") {
        return (
            <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 shrink-0">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 8v5M12 16h.01" />
            </svg>
        )
    }
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 shrink-0">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 11v5M12 8h.01" />
        </svg>
    )
}

export const ToastProvider = ({ children }: { children: ReactNode }) => {
    const [toasts, setToasts] = useState<Toast[]>([])
    const idRef = useRef(0)

    const remove = useCallback((id: number) => {
        setToasts((prev) => prev.filter((t) => t.id !== id))
    }, [])

    const notify = useCallback(
        (message: string, type: ToastType = "info") => {
            const id = ++idRef.current
            setToasts((prev) => [...prev, { id, type, message }])
            setTimeout(() => remove(id), 5000)
        },
        [remove]
    )

    return (
        <ToastContext.Provider value={{ notify }}>
            {children}

            <div className="pointer-events-none fixed bottom-4 left-1/2 z-[9999] flex w-[calc(100%-2rem)] max-w-md -translate-x-1/2 flex-col gap-2">
                {toasts.map((t) => (
                    <div
                        key={t.id}
                        className="toast-card pointer-events-auto flex items-start gap-3 rounded-2xl border border-gray-100 bg-white px-4 py-3 shadow-lg"
                        style={{ borderLeft: `4px solid ${TONE[t.type].border}` }}
                    >
                        <span
                            className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
                            style={{ background: TONE[t.type].ring }}
                        >
                            <ToastIcon type={t.type} />
                        </span>
                        <p className="flex-1 text-sm font-medium leading-snug text-figmaBlack">
                            {t.message}
                        </p>
                        <button
                            type="button"
                            onClick={() => remove(t.id)}
                            aria-label="Închide"
                            className="shrink-0 text-figmaDarkGray transition-colors hover:text-figmaBlack"
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="h-4 w-4">
                                <path d="M6 6l12 12M18 6L6 18" />
                            </svg>
                        </button>
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    )
}
